import React, { useState, useEffect } from 'react';
import { Check, X } from 'lucide-react';
import MathText from '../../../../../VueQuestion/MathText';
import { answerTheme, marqueSigne } from '../../../../../VueQuestion/interactifs/quizTheme';
import { etatsTableau, etatCase, LegendeAttendu } from '../../../../../VueQuestion/interactifs/corrige';

/**
 * Composant pour afficher un tableau de réponses interactif côté élève (Quiz)
 * Version avec design immersif dark pour l'espace élève
 *
 * `corrige` (correction sur place, lot 3 du 2026-09-21) : n'arrive que si la
 * réponse est fausse (contrat dans interactifs/corrige.jsx). Le tableau ne
 * passe pas par le routeur QuestionInteractive, c'est Quiz et l'aperçu prof
 * qui le lui passent en direct. Chaque case se marque alors avec les trois
 * classes d'answerTheme : juste (✓), faux (✗), attendu (pointillé, vide) ;
 * le glyphe est le signe du verdict, plus la coche de la colonne. Barre de
 * progression, main et point qui clignote s'effacent : on ne répond plus, on
 * lit. Sans `corrige`, le rendu d'avant, inchangé.
 */
const TableResponseElementEleve = ({ config, onAnswerChange, reponseExistante, setHasAnswered, readOnly = false, light = false, compact = false, corrige = null }) => {
    const {
        type = 'vraiFaux',
        titre = '',
        sousTitre = '',
        colonnes = ['Vrai', 'Faux'],
        lignes = [],
        bonnesReponses = []
    } = config || {};

    const [reponses, setReponses] = useState(() => {
        if (!reponseExistante || !lignes.length) return {};

        const parsed = {};
        lignes.forEach((ligne, ligneIdx) => {
            colonnes.forEach((colonne, colonneIdx) => {
                if (reponseExistante.includes(`[${ligne}|${colonne}]`)) {
                    parsed[`${ligneIdx}-${colonneIdx}`] = true;
                }
            });
        });
        return parsed;
    });

    useEffect(() => {
     
        if (!reponseExistante || reponseExistante === "") {
          
            setReponses({});
        } else {
            const parsed = {};
            lignes.forEach((ligne, ligneIdx) => {
                colonnes.forEach((colonne, colonneIdx) => {
                    const pattern = `[${ligne}|${colonne}]`;
                    const found = reponseExistante.includes(pattern);
                  
                    if (found) {
                        parsed[`${ligneIdx}-${colonneIdx}`] = true;
                    }
                });
            });
         
            setReponses(parsed);
        }
    }, [reponseExistante, lignes, colonnes]);

    // Correction : par ligne, la case cochée et la case attendue. Figé = plus
    // d'interaction ni d'aides à répondre, comme en lecture seule.
    const etats = corrige ? etatsTableau(corrige, lignes, colonnes) : null;
    const t = answerTheme(light);
    const fige = readOnly || Boolean(corrige);
    const caseNeutre = light ? 'bg-gray-100 border-gray-300' : 'bg-slate-600/50 border-slate-500';

    const handleCellClick = (ligneIndex, colonneIndex) => {
        if (fige) return;
        const newReponses = { ...reponses };

        if (type === 'association') {
            colonnes.forEach((_, idx) => {
                delete newReponses[`${ligneIndex}-${idx}`];
            });
            lignes.forEach((_, idx) => {
                delete newReponses[`${idx}-${colonneIndex}`];
            });
        } else {
            colonnes.forEach((_, idx) => {
                delete newReponses[`${ligneIndex}-${idx}`];
            });
        }

        const key = `${ligneIndex}-${colonneIndex}`;
        if (!reponses[key]) {
            newReponses[key] = true;
        }

        setReponses(newReponses);

        const allAnswered = lignes.every((_, idx) =>
            colonnes.some((_, colIdx) => newReponses[`${idx}-${colIdx}`])
        );

        if (allAnswered) {
            let formattedAnswer = "";
            lignes.forEach((ligne, idx) => {
                const colonneChoisie = colonnes.findIndex((_, colIdx) =>
                    newReponses[`${idx}-${colIdx}`]
                );
                if (colonneChoisie !== -1) {
                    formattedAnswer += `[${ligne}|${colonnes[colonneChoisie]}]`;
                }
            });
            onAnswerChange(formattedAnswer);
        } else {
            setHasAnswered && setHasAnswered(false);
            onAnswerChange("");
        }
    };

    const answeredCount = lignes.filter((_, idx) =>
        colonnes.some((_, colIdx) => reponses[`${idx}-${colIdx}`])
    ).length;
    const progressPercent = lignes.length > 0 ? (answeredCount / lignes.length) * 100 : 0;

    if (!lignes.length || !colonnes.length) {
        return (
            <div className="p-4 bg-amber-500/10 rounded-xl border border-amber-500/30">
                <p className="text-amber-400">Configuration du tableau invalide</p>
            </div>
        );
    }

    // La largeur minimale des colonnes de réponse avait été taillée pour des
    // libellés en toutes lettres (« Multiplication »). Avec des en-têtes courts
    // (+ − × ÷) elle réservait 360 px pour quatre glyphes et écrasait la colonne
    // des énoncés, qui n'a elle aucune largeur propre : texte comprimé sur 3 ou
    // 4 mots par ligne (retour utilisateur, 2026-08-02). Seuil à 3 caractères,
    // donc « Vrai » / « Faux » gardent leur largeur d'origine.
    const enTetesCourts = colonnes.every((c) => String(c).trim().length <= 3);
    const largeurReponse = enTetesCourts
        ? (compact ? 'min-w-[40px] sm:min-w-[46px]' : 'min-w-[52px] sm:min-w-[64px]')
        : (compact ? 'min-w-[50px] sm:min-w-[60px]' : 'min-w-[70px] sm:min-w-[90px]');
    const padReponse = enTetesCourts
        ? (compact ? 'px-1 py-1.5 sm:p-2' : 'px-1.5 py-3 sm:px-2 sm:py-4')
        : (compact ? 'p-1.5 sm:p-2' : 'p-3 sm:p-4');
    // Padding HORIZONTAL réduit sur la colonne des énoncés : 16 px de chaque
    // côté, c'est une ligne de texte perdue quand la colonne est étroite.
    const padEnonce = compact ? 'px-1.5 py-1.5 sm:px-2 sm:py-2' : 'px-2 py-3 sm:px-3 sm:py-4';

    return (
        <div className="w-full">
            <div className={`backdrop-blur rounded-xl ${compact ? 'p-2 sm:p-3' : 'p-4 sm:p-6'} ${light ? 'bg-white border border-gray-200' : 'bg-slate-700/50 border border-slate-600'}`}>
                {titre && (
                    <h2 className={`font-bold mb-2 ${compact ? 'text-sm sm:text-base' : 'text-lg sm:text-xl'} ${light ? 'text-blue-800' : 'text-cyan-400'}`}>
                        {titre}
                    </h2>
                )}
                {sousTitre && (
                    <p className={`mb-4 ${compact ? 'text-xs sm:text-sm' : 'text-sm sm:text-base'} ${light ? 'text-gray-600' : 'text-slate-300'}`}>
                        {sousTitre}
                    </p>
                )}

                {/* Barre de progression — masquée en readOnly et en correction */}
                {!fige && (
                    <div className="mb-4">
                        <div className="flex items-center justify-between mb-2">
                            <span className={`text-xs ${light ? 'text-gray-400' : 'text-slate-400'}`}>Progression</span>
                            <span className={`text-xs font-medium ${light ? 'text-blue-600' : 'text-cyan-400'}`}>{answeredCount}/{lignes.length}</span>
                        </div>
                        <div className={`h-2 rounded-full overflow-hidden ${light ? 'bg-gray-200' : 'bg-slate-600'}`}>
                            <div
                                className="h-full bg-gradient-to-r from-blue-500 to-cyan-500 transition-all duration-500 ease-out rounded-full"
                                style={{ width: `${progressPercent}%` }}
                            />
                        </div>
                    </div>
                )}

                {/* Tableau */}
                <div className={`overflow-x-auto rounded-xl border shadow-lg ${light ? 'border-gray-200 shadow-blue-100/50' : 'border-slate-600 shadow-blue-500/10'}`}>
                    <table className="w-full">
                        <thead>
                            <tr className={light ? 'bg-gradient-to-r from-blue-100 via-blue-50 to-sky-50' : 'bg-gradient-to-r from-blue-600/80 via-blue-500/80 to-cyan-500/80'}>
                                {/* width:100% → la colonne des énoncés absorbe tout
                                    l'espace libre, les colonnes de réponse tombent
                                    à leur largeur minimale. */}
                                <th
                                    style={{ width: '100%' }}
                                    className={`border-b border-r text-left font-bold ${padEnonce} ${compact ? 'text-[10px] sm:text-xs' : 'text-sm sm:text-base'} ${light ? 'border-gray-200 text-blue-900' : 'border-slate-500/50 text-white'}`}
                                >
                                </th>
                                {colonnes.map((colonne, index) => (
                                    <th
                                        key={index}
                                        className={`border-b border-r last:border-r-0 text-center font-bold whitespace-nowrap ${padReponse} ${largeurReponse} ${compact ? 'text-[9px] sm:text-[11px]' : 'text-xs sm:text-sm'} ${enTetesCourts ? 'text-lg sm:text-xl' : ''} ${light ? 'border-gray-200 text-blue-800' : 'border-slate-500/50 text-white'}`}
                                        title={colonne}
                                    >
                                        {compact && !enTetesCourts ? <span className="truncate block max-w-[60px]">{colonne}</span> : colonne}
                                    </th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {lignes.map((ligne, ligneIdx) => {
                                // En correction, la ligne ne dit plus « répondue » : ce sont ses cases qui parlent.
                                const isRowAnswered = !corrige && colonnes.some((_, colIdx) => reponses[`${ligneIdx}-${colIdx}`]);
                                return (
                                    <tr
                                        key={ligneIdx}
                                        className={`transition-colors duration-200 ${
                                            isRowAnswered
                                                ? light ? 'bg-blue-50' : 'bg-blue-500/10'
                                                : ligneIdx % 2 === 0
                                                    ? light ? 'bg-white' : 'bg-slate-800/50'
                                                    : light ? 'bg-gray-50' : 'bg-slate-700/30'
                                        } ${corrige ? '' : (light ? 'hover:bg-blue-50/80' : 'hover:bg-slate-600/50')}`}
                                    >
                                        <td className={`border-b border-r font-medium leading-relaxed ${padEnonce} ${compact ? 'text-[10px] sm:text-xs' : 'text-xs sm:text-sm'} ${light ? 'border-gray-200 text-gray-800' : 'border-slate-600/50 text-slate-200'}`}>
                                            <div className="flex items-center gap-1.5">
                                                {isRowAnswered && (
                                                    <span className={`rounded-full animate-pulse ${compact ? 'w-1.5 h-1.5' : 'w-2 h-2'} ${light ? 'bg-blue-500' : 'bg-cyan-400'}`}></span>
                                                )}
                                                <MathText>{ligne}</MathText>
                                            </div>
                                        </td>
                                        {colonnes.map((_, colonneIdx) => {
                                            const isSelected = reponses[`${ligneIdx}-${colonneIdx}`];
                                            const etat = etats ? etatCase(etats[ligneIdx], colonneIdx) : null;
                                            return (
                                                <td
                                                    key={colonneIdx}
                                                    className={`border-b border-r last:border-r-0 text-center ${enTetesCourts ? 'px-1 py-2 sm:px-1.5 sm:py-3' : (compact ? 'p-1 sm:p-1.5' : 'p-2 sm:p-3')} ${light ? 'border-gray-200' : 'border-slate-600/50'}`}
                                                >
                                                    {etats ? (
                                                        <button
                                                            type="button"
                                                            disabled
                                                            aria-label={etat || undefined}
                                                            data-testid={`case-${ligneIdx}-${colonneIdx}`}
                                                            className={`rounded-lg inline-flex items-center justify-center border-2 font-black cursor-default
                                                                ${compact ? 'w-6 h-6 sm:w-7 sm:h-7 text-sm' : 'w-8 h-8 sm:w-10 sm:h-10 text-lg'}
                                                                ${etat ? t[etat] : caseNeutre}`}
                                                        >
                                                            {marqueSigne(etat)}
                                                        </button>
                                                    ) : (
                                                        <button
                                                            onClick={() => handleCellClick(ligneIdx, colonneIdx)}
                                                            className={`rounded-lg inline-flex items-center justify-center transition-all duration-300 transform hover:scale-110
                                                                ${compact ? 'w-6 h-6 sm:w-7 sm:h-7' : 'w-8 h-8 sm:w-10 sm:h-10'}
                                                                ${isSelected
                                                                    ? 'bg-gradient-to-br from-blue-500 to-cyan-500 shadow-lg shadow-blue-500/40 scale-105'
                                                                    : light
                                                                        ? 'bg-gray-100 border-2 border-gray-300 hover:border-blue-400 hover:bg-blue-50'
                                                                        : 'bg-slate-600/50 border-2 border-slate-500 hover:border-blue-400 hover:bg-slate-600'}`}
                                                        >
                                                            {isSelected && (
                                                                colonnes.length === 2 && type === 'vraiFaux' ? (
                                                                    colonneIdx === 0 ?
                                                                        <Check className={`text-white ${compact ? 'w-3.5 h-3.5' : 'w-5 h-5'}`} /> :
                                                                        <X className={`text-white ${compact ? 'w-3.5 h-3.5' : 'w-5 h-5'}`} />
                                                                ) : (
                                                                    <Check className={`text-white ${compact ? 'w-3.5 h-3.5' : 'w-5 h-5'}`} />
                                                                )
                                                            )}
                                                        </button>
                                                    )}
                                                </td>
                                            );
                                        })}
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>

                {/* Légende de la correction : une case pointillée n'a pas la place d'une étiquette */}
                {etats ? <LegendeAttendu clair={light} /> : null}

                {/* Message d'aide — masqué en readOnly et en correction */}
                {!fige && (
                    <div className="mt-4 text-center">
                        <p className={`text-xs sm:text-sm flex items-center justify-center gap-2 ${light ? 'text-gray-400' : 'text-slate-400'}`}>
                            <span>👆</span>
                            {answeredCount < lignes.length
                                ? `Clique sur une case par ligne pour répondre`
                                : <span className={`font-medium ${light ? 'text-blue-600' : 'text-cyan-400'}`}>Toutes les réponses sont sélectionnées !</span>
                            }
                        </p>
                    </div>
                )}
            </div>
        </div>
    );
};

export default TableResponseElementEleve;
